import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ko from "date-fns/locale/ko";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Input from "components/atoms/Input";
import Icon from "components/atoms/Icon";
import { Tag } from "components/atoms";
import Dropdown, {
  DropdownMenuItem,
  DropdownMenuItems,
} from "components/molecules/Dropdown";
import { DatePickerButton } from "components/molecules/DateAndTimePicker";
import { Schedule } from "common/models/types";
import {
  CreateScheduleParams,
  UpdateScheduleParams,
} from "api/scheduleService";

// --- 시간 옵션 (30분 단위, 08:00~21:30) ---
interface TimeOption {
  label: string;
  hour: number;
  minute: number;
}

const generateTimeOptions = (): TimeOption[] => {
  const options: TimeOption[] = [];
  for (let h = 8; h <= 21; h++) {
    const period = h < 12 ? "오전" : "오후";
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    options.push({
      label: `${period} ${displayHour}:00`,
      hour: h,
      minute: 0,
    });
    options.push({
      label: `${period} ${displayHour}:30`,
      hour: h,
      minute: 30,
    });
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const DEFAULT_TIME = TIME_OPTIONS.find((o) => o.hour === 9 && o.minute === 0) || TIME_OPTIONS[0];

const getTimeOptionFromDate = (date: Date): TimeOption => {
  const h = date.getHours();
  const m = date.getMinutes() >= 30 ? 30 : 0;
  return TIME_OPTIONS.find((o) => o.hour === h && o.minute === m) || DEFAULT_TIME;
};

const getInitialTime = (): TimeOption => {
  const now = new Date();
  const nextHour = now.getHours() + 1;
  if (nextHour < 8) return DEFAULT_TIME;
  if (nextHour > 21) return TIME_OPTIONS[TIME_OPTIONS.length - 1];
  return TIME_OPTIONS.find((o) => o.hour === nextHour && o.minute === 0) || DEFAULT_TIME;
};

const formatLocalDateTime = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
};

const formatScheduleDate = (date: Date): string => {
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const day = dayNames[date.getDay()];
  return `${y}-${m}-${d} (${day})`;
};

const formatScheduleTime = (date: Date): string => {
  const h = date.getHours();
  const min = date.getMinutes();
  const period = h < 12 ? "오전" : "오후";
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${period} ${displayHour}:${String(min).padStart(2, "0")}`;
};

const getDdayInfo = (date: Date): { label: string; color: "Red" | "Orange" | "Blue" | "Gray" } | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diff < 0) return { label: `${Math.abs(diff)}일 지남`, color: "Red" };
  if (diff === 0) return { label: "오늘", color: "Orange" };
  if (diff === 1) return { label: "내일", color: "Orange" };
  if (diff <= 7) return { label: `${diff}일 후`, color: "Blue" };
  return null;
};

const FORM_HEIGHT = "36px";
const SCHEDULE_CARD_HEIGHT = "130px";


// --- Props ---
interface ScheduleSectionProps {
  schedules: Schedule[];
  onCreateSchedule: (data: CreateScheduleParams) => void;
  onUpdateSchedule: (scheduleId: number, data: UpdateScheduleParams) => void;
  onDeleteSchedule: (scheduleId: number) => void;
}

// --- Styled Components ---

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  min-height: ${SCHEDULE_CARD_HEIGHT};
  min-width: 200px;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* Input 컴포넌트 내부 InputWrapper(height:48px) 오버라이드 */
const CompactInput = styled.div`
  flex: 1;
  min-width: 120px;

  /* Wrapper > InputWrapper */
  & > div > div {
    height: ${FORM_HEIGHT};
    padding: 4px 12px;
    border-radius: 8px;
    box-sizing: border-box;
  }

  input, textarea {
    font-size: 12px !important;
  }
`;

const DatePickerWrapper = styled.div`
  z-index: 9999;

  .react-datepicker {
    width: 240px;
    padding: 8px;
    border: none;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  }

  .react-datepicker__month-container {
    width: 100%;
  }

  .react-datepicker__header {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    border-bottom: none;
    padding: 8px 0 0;
  }

  .react-datepicker__header__dropdown {
    display: none;
  }

  /* 월 표시 + 좌우 화살표를 한 줄에 배치 */
  .react-datepicker__month-container > .react-datepicker__header {
    position: relative;
  }

  .react-datepicker__current-month {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 32px;
    margin-bottom: 8px;
  }

  .react-datepicker__navigation {
    top: 16px;
    width: 32px;
    height: 32px;
  }

  .react-datepicker__navigation--previous {
    left: 20px;
  }

  .react-datepicker__navigation--next {
    right: 20px;
  }

  .react-datepicker__day-name {
    height: 32px;
    width: 32px;
    font-weight: 500;
    line-height: 32px;
    font-size: 13px;
    padding: 0;
    margin: 0;
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }

  .react-datepicker__day-names {
    margin: 0;
    padding: 0;
    height: 32px;
  }

  .react-datepicker__week {
    display: flex;
  }

  .react-datepicker__day {
    height: 32px;
    line-height: 32px;
    flex: 1 1 33%;
    font-size: 13px;
    color: ${COLOR_SYSTEM.get("Gray")?.[900]};
    margin: 0;
    border-radius: 0;
  }

  .react-datepicker__day:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    background-color: ${COLOR_SYSTEM.get("Skyblue")?.[400]};
    border-radius: 50%;
  }

  .react-datepicker__day--outside-month {
    visibility: hidden;
  }

  .react-datepicker__input-container {
    display: inline-block;
  }
`;

const ScheduleListContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
`;

const ScheduleCard = styled.div<{ isDone?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: 240px;
  height: ${SCHEDULE_CARD_HEIGHT};
  padding: 10px 12px;
  border: 1px solid ${({ isDone }) =>
    isDone ? COLOR_SYSTEM.get("Gray")?.[50] : COLOR_SYSTEM.get("Skyblue")?.[200]};
  border-radius: 8px;
  background: ${({ isDone }) =>
    isDone ? COLOR_SYSTEM.get("Gray")?.[10] : COLOR_SYSTEM.get("Gray")?.[0]};
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const ScheduleCardDate = styled.span<{ isDone?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ isDone }) =>
    isDone ? COLOR_SYSTEM.get("Gray")?.[200] : COLOR_SYSTEM.get("Gray")?.[800]};
`;

const MoreButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 4px;
  min-width: 160px;
  z-index: 1000;
  white-space: nowrap;
`;

const MenuItem = styled.button<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: ${({ danger }) =>
    danger ? COLOR_SYSTEM.get("Red")?.[500] : COLOR_SYSTEM.get("Gray")?.[700]};
  border-radius: 6px;
  transition: background 0.2s;
  text-align: left;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const ScheduleFormRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${SCHEDULE_CARD_HEIGHT};
`;

const NoteText = styled.span<{ isDone?: boolean }>`
  font-size: 11px;
  color: ${({ isDone }) =>
    isDone ? COLOR_SYSTEM.get("Gray")?.[200] : COLOR_SYSTEM.get("Skyblue")?.[500]};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 2px;
`;

// --- Component ---
const ScheduleSection: React.FC<ScheduleSectionProps> = ({
  schedules,
  onCreateSchedule,
  onUpdateSchedule,
  onDeleteSchedule,
}) => {
  // 추가 폼 상태
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<TimeOption>(getInitialTime);
  const [notes, setNotes] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const timeDropdownRef = useRef<HTMLDivElement>(null);

  // schedules 변경 시 (환자 전환) 폼 초기화
  useEffect(() => {
    setIsFormOpen(false);
    setIsCalendarOpen(false);
    setEditingId(null);
    setIsEditCalendarOpen(false);
    setMenuOpenId(null);
    setNotes("");
  }, [schedules]);

  // 수정 상태
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDate, setEditDate] = useState<Date>(new Date());
  const [editTime, setEditTime] = useState<TimeOption>(DEFAULT_TIME);
  const [editNotes, setEditNotes] = useState("");
  const [isEditCalendarOpen, setIsEditCalendarOpen] = useState(false);
  const [isEditTimeDropdownOpen, setIsEditTimeDropdownOpen] = useState(false);
  const editTimeDropdownRef = useRef<HTMLDivElement>(null);

  // ⋯ 메뉴 상태
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 시간 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(e.target as Node)
      ) {
        setIsTimeDropdownOpen(false);
      }
      if (
        editTimeDropdownRef.current &&
        !editTimeDropdownRef.current.contains(e.target as Node)
      ) {
        setIsEditTimeDropdownOpen(false);
      }
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 정렬된 일정 목록 (가까운 날짜 우선)
  const sortedSchedules = [...schedules].sort((a, b) => {
    const dateA = new Date(a.scheduledAt || 0).getTime();
    const dateB = new Date(b.scheduledAt || 0).getTime();
    return dateA - dateB;
  });

  const handleSubmit = () => {
    const scheduledDate = new Date(selectedDate);
    scheduledDate.setHours(selectedTime.hour, selectedTime.minute, 0, 0);
    const scheduledAt = formatLocalDateTime(scheduledDate);

    // 같은 날짜에 이미 일정이 있는지 확인
    const sameDay = schedules.some((s) => {
      const existing = new Date(s.scheduledAt || 0);
      return (
        existing.getFullYear() === scheduledDate.getFullYear() &&
        existing.getMonth() === scheduledDate.getMonth() &&
        existing.getDate() === scheduledDate.getDate()
      );
    });

    if (sameDay && !window.confirm("해당 날짜에 이미 일정이 있습니다. 추가하시겠습니까?")) {
      return;
    }

    onCreateSchedule({
      scheduledAt,
      notes: notes.trim() || undefined,
    });

    setNotes("");
    setIsFormOpen(false);
  };

  const handleToggleDone = (schedule: Schedule) => {
    if (!schedule.id) return;
    onUpdateSchedule(schedule.id, {
      doneAt: schedule.doneAt ? null : 1,
    });
  };

  const handleStartEdit = (schedule: Schedule) => {
    if (!schedule.id) return;
    const date = new Date(schedule.scheduledAt || new Date());
    setEditingId(schedule.id);
    setEditDate(date);
    setEditTime(getTimeOptionFromDate(date));
    setEditNotes(schedule.notes || "");
  };

  const handleSaveEdit = () => {
    if (!editingId) return;

    const scheduledDate = new Date(editDate);
    scheduledDate.setHours(editTime.hour, editTime.minute, 0, 0);
    const scheduledAt = formatLocalDateTime(scheduledDate);

    onUpdateSchedule(editingId, {
      scheduledAt,
      notes: editNotes.trim() || null,
    });

    setEditingId(null);
    setIsEditTimeDropdownOpen(false);
  };


  const renderTimeDropdown = (
    selected: TimeOption,
    isOpen: boolean,
    onToggle: () => void,
    onSelect: (option: TimeOption) => void,
    ref: React.RefObject<HTMLDivElement>,
  ) => (
    <div ref={ref}>
      <Dropdown
        defaultValue="시간 선택"
        selected={selected.label}
        size="Small"
        buttonCustomStyle={{
          color: COLOR_SYSTEM.get("Gray")[700],
          fontSize: "13px",
          padding: "0px 10px",
          width: "110px",
          height: FORM_HEIGHT,
          borderRadius: "8px",
          backgroundColor: COLOR_SYSTEM.get("Gray")[20],
          border: "none",
        }}
        onClick={onToggle}
        active={isOpen}
        customStyle={{
          height: "300px",
          width: "110px",
          borderRadius: "8px",
          padding: "0px",
          margin: "0px",
          scrollBehavior: "auto",
        }}
      >
        <DropdownMenuItems>
          {TIME_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={`${option.hour}-${option.minute}`}
              isSelected={
                selected.hour === option.hour &&
                selected.minute === option.minute
              }
              onClick={(e) => {
                e.stopPropagation();
                onSelect(option);
              }}
            >
              <Text
                textType="InputMedium"
                color={COLOR_SYSTEM.get("Gray")[700]}
                style={{ whiteSpace: "nowrap", fontSize: "13px" }}
              >
                {option.label}
              </Text>
            </DropdownMenuItem>
          ))}
        </DropdownMenuItems>
      </Dropdown>
    </div>
  );

  return (
    <div style={{ position: "relative", marginTop: "8px" }}>
      {/* 일정 목록 */}
      <ScheduleListContainer>
        {sortedSchedules.length > 0 ? (
          sortedSchedules.map((schedule, index) => {
            const scheduledAt = new Date(schedule.scheduledAt || 0);
            const isDone = !!schedule.doneAt;
            const isEditing = editingId === schedule.id;
            const visitLabel = `${index + 1}차 방문`;

            if (isEditing) {
              return (
                <ScheduleFormRow key={schedule.id}>
                  <FormContainer style={{ flex: 1, marginBottom: 0 }}>
                    <FormRow>
                      <DatePickerWrapper>
                        <DatePicker
                          locale={ko}
                          dateFormat="yyyy-MM-dd (eee)"
                          dateFormatCalendar="yyyy년 MM월"
                          selected={editDate}
                          open={isEditCalendarOpen}
                          onClickOutside={() => setIsEditCalendarOpen(false)}
                          onChange={(date: Date) => {
                            if (date) setEditDate(date);
                            setIsEditCalendarOpen(false);
                          }}
                          customInput={
                            <DatePickerButton
                              isOpen={isEditCalendarOpen}
                              onToggle={() => setIsEditCalendarOpen((prev) => !prev)}
                            />
                          }
                        />
                      </DatePickerWrapper>

                      {renderTimeDropdown(
                        editTime,
                        isEditTimeDropdownOpen,
                        () => setIsEditTimeDropdownOpen(!isEditTimeDropdownOpen),
                        (option) => {
                          setEditTime(option);
                          setIsEditTimeDropdownOpen(false);
                        },
                        editTimeDropdownRef,
                      )}
                    </FormRow>

                    <FormRow>
                      <CompactInput>
                        <Input
                          value={editNotes}
                          onChange={(e) => setEditNotes(e.target.value)}
                          placeholder="예: 자택방문, 통화, 약국방문 등"
                          style={{
                            fontSize: "13px",
                          }}
                          backgroundColor={COLOR_SYSTEM.get("Gray")[20]}
                          onEnter={() => handleSaveEdit()}
                        />
                      </CompactInput>

                      <Button
                        size="Small"
                        type="Text"
                        variant="Contained"
                        color="Primary"
                        onClick={handleSaveEdit}
                        style={{ height: FORM_HEIGHT, whiteSpace: "nowrap" }}
                      >
                        저장
                      </Button>
                      <Button
                        size="Small"
                        type="Text"
                        variant="Outlined"
                        color="Tertiary"
                        onClick={() => {
                          setEditingId(null);
                          setIsEditTimeDropdownOpen(false);
                        }}
                        style={{ height: FORM_HEIGHT, whiteSpace: "nowrap" }}
                      >
                        취소
                      </Button>
                    </FormRow>
                  </FormContainer>
                </ScheduleFormRow>
              );
            }

            return (
              <ScheduleCard key={schedule.id} isDone={isDone}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: isDone ? COLOR_SYSTEM.get("Gray")?.[200] : COLOR_SYSTEM.get("Gray")?.[400] }}>
                      {visitLabel}
                    </span>
                    {isDone ? (
                      <Tag variant="Tinted" color="Green" size="Small">완료</Tag>
                    ) : (() => {
                      const dday = getDdayInfo(scheduledAt);
                      return dday ? <Tag variant="Tinted" color={dday.color} size="Small">{dday.label}</Tag> : null;
                    })()}
                  </div>
                  <div ref={menuOpenId === schedule.id ? menuRef : null} style={{ position: "relative" }}>
                    <MoreButton onClick={() => setMenuOpenId(menuOpenId === schedule.id ? null : schedule.id!)}>
                      <Icon name="Dots Vertical" size={16} color={COLOR_SYSTEM.get("Gray")?.[400]} />
                    </MoreButton>
                    {menuOpenId === schedule.id && (
                      <DropdownMenu>
                        {isDone && (
                          <MenuItem onClick={() => { handleToggleDone(schedule); setMenuOpenId(null); }}>
                            완료 취소
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => { handleStartEdit(schedule); setMenuOpenId(null); }}>
                          수정 (일정, 메모)
                        </MenuItem>
                        <MenuItem danger onClick={() => { if (schedule.id) onDeleteSchedule(schedule.id); setMenuOpenId(null); }}>
                          일정 삭제
                        </MenuItem>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                <ScheduleCardDate isDone={isDone}>
                  {formatScheduleDate(scheduledAt)} {formatScheduleTime(scheduledAt)}
                </ScheduleCardDate>
                {schedule.notes && (
                  <NoteText isDone={isDone}>{schedule.notes}</NoteText>
                )}
                {!isDone && (
                  <div style={{ marginTop: "auto", paddingTop: "8px", alignSelf: "flex-end" }}>
                    <Button
                      size="Small"
                      type="Text"
                      variant="Contained"
                      color="Primary"
                      onClick={() => handleToggleDone(schedule)}
                      style={{ fontSize: "11px", height: "28px" }}
                    >
                      방문 완료
                    </Button>
                  </div>
                )}
              </ScheduleCard>
            );
          })
        ) : null}
        {isFormOpen ? (
          <ScheduleFormRow onClick={(e) => e.stopPropagation()}>
            <FormContainer style={{ flex: 1, marginBottom: 0 }} onClick={(e) => e.stopPropagation()}>
              <FormRow>
                <DatePickerWrapper>
                  <DatePicker
                    locale={ko}
                    dateFormat="yyyy-MM-dd (eee)"
                    dateFormatCalendar="yyyy년 MM월"
                    selected={selectedDate}
                    open={isCalendarOpen}
                    onClickOutside={() => setIsCalendarOpen(false)}
                    onChange={(date: Date) => {
                      if (date) setSelectedDate(date);
                      setIsCalendarOpen(false);
                    }}
                    customInput={
                      <DatePickerButton
                        isOpen={isCalendarOpen}
                        onToggle={() => setIsCalendarOpen((prev) => !prev)}
                      />
                    }
                  />
                </DatePickerWrapper>
                {renderTimeDropdown(
                  selectedTime,
                  isTimeDropdownOpen,
                  () => setIsTimeDropdownOpen(!isTimeDropdownOpen),
                  (option) => {
                    setSelectedTime(option);
                    setIsTimeDropdownOpen(false);
                  },
                  timeDropdownRef,
                )}
              </FormRow>
              <FormRow>
                <CompactInput>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="예: 자택방문, 통화, 약국방문 등"
                    style={{ fontSize: "13px" }}
                    backgroundColor={COLOR_SYSTEM.get("Gray")[20]}
                    onEnter={() => handleSubmit()}
                  />
                </CompactInput>
                <Button
                  size="Small"
                  type="Text"
                  variant="Contained"
                  color="Primary"
                  onClick={handleSubmit}
                  style={{ height: FORM_HEIGHT, whiteSpace: "nowrap" }}
                >
                  등록
                </Button>
                <Button
                  size="Small"
                  type="Text"
                  variant="Outlined"
                  color="Tertiary"
                  onClick={() => setIsFormOpen(false)}
                  style={{ height: FORM_HEIGHT, whiteSpace: "nowrap" }}
                >
                  취소
                </Button>
              </FormRow>
            </FormContainer>
          </ScheduleFormRow>
        ) : (
          <ScheduleCard
            isDone={false}
            onClick={() => setIsFormOpen(true)}
            style={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: SCHEDULE_CARD_HEIGHT,
              border: `1px dashed ${COLOR_SYSTEM.get("Gray")?.[200]}`,
              gap: "6px",
            }}
          >
            <Icon name="Plus" size={20} color={COLOR_SYSTEM.get("Gray")?.[400]} />
            <span style={{ fontSize: "12px", color: COLOR_SYSTEM.get("Gray")?.[400] }}>
              새 방문일정 추가
            </span>
          </ScheduleCard>
        )}
      </ScheduleListContainer>
    </div>
  );
};

export default ScheduleSection;
